import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './edit-city-modal.module.scss';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import {
  InputText,
  InputTextErrorMode
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { CityModel } from '../../../../../shared/models/city.model';

export interface EditCityModalProps {
  data: {
    city?: CityModel;
  };
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    cancelHandler: () => void;
    editCityHandler: (cityId: string, city: Pick<CityModel, 'name' | 'nameAr'>) => void;
  };
}

/**
 * functional component EditCityModal
 * @param {EditCityModalProps}
 */
export const EditCityModal: React.FC<EditCityModalProps> = ({ data, config, eventHandlers }) => {
  const [name, setName] = useState<FormElementData<string>>({
    value: '',
    isValid: true,
    changed: false
  });
  const [nameAr, setNameAr] = useState<FormElementData<string>>({
    value: '',
    isValid: true,
    changed: false
  });
  const { t } = useTranslation();
  const isMobileView = useWindowResizer();

  useEffect(() => {
    setName({ value: data.city?.name || '', isValid: true, changed: false });
    setNameAr({ value: data.city?.nameAr || '', isValid: true, changed: false });
  }, [data.city]);

  const canSubmit = (name.changed || nameAr.changed) && name.value.trim() !== '';

  const handleSubmit = () => {
    if (data.city && canSubmit) {
      eventHandlers.editCityHandler(data.city.id, {
        name: name.value.trim(),
        nameAr: nameAr.value.trim()
      });
    }
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{ closeModalHandler: eventHandlers.cancelHandler }}
    >
      <div className={styles['edit-city-modal']}>
        <div className={styles['edit-city-modal__title']}>
          {t('CITY_MODULE.LIST_CONTAINER.EDIT_CITY_MODAL.TITLE')}
        </div>
        <div className={styles['edit-city-modal__description']}>
          {t('CITY_MODULE.LIST_CONTAINER.EDIT_CITY_MODAL.NAME_LABEL')}
        </div>
        <InputText
          data={{
            value: name.value,
            errorMessage: '',
            placeholder: t('CITY_MODULE.LIST_CONTAINER.EDIT_CITY_MODAL.NAME_PLACEHOLDER')
          }}
          config={{ maxRows: 1, autoFocus: true, showErrorMode: InputTextErrorMode.instant }}
          eventHandlers={{
            onChangeHandler: (value: string) => {
              setName({ value, isValid: true, changed: true });
            },
            onEnterPressedHandler: handleSubmit
          }}
        />
        <div className={styles['edit-city-modal__description']}>
          {t('CITY_MODULE.LIST_CONTAINER.EDIT_CITY_MODAL.NAME_AR_LABEL')}
        </div>
        <InputText
          data={{
            value: nameAr.value,
            errorMessage: '',
            placeholder: t('CITY_MODULE.LIST_CONTAINER.EDIT_CITY_MODAL.NAME_AR_PLACEHOLDER')
          }}
          config={{ maxRows: 1, showErrorMode: InputTextErrorMode.instant }}
          eventHandlers={{
            onChangeHandler: (value: string) => {
              setNameAr({ value, isValid: true, changed: true });
            },
            onEnterPressedHandler: handleSubmit
          }}
        />
      </div>
      <div className={styles['edit-city-modal--actions']}>
        <Button
          onClick={eventHandlers.cancelHandler}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          type={ButtonType.primaryInverted}
        >
          {t('CITY_MODULE.LIST_CONTAINER.EDIT_CITY_MODAL.CANCEL_BUTTON')}
        </Button>
        <Button
          disabled={!canSubmit}
          onClick={handleSubmit}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          {t('CITY_MODULE.LIST_CONTAINER.EDIT_CITY_MODAL.SUBMIT_BUTTON')}
        </Button>
      </div>
    </Modal>
  );
};
